import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import { Clapperboard, X, Loader2, CheckCircle, Play, Download, Eye, Plus, ImagePlus, Film, Music, RefreshCw, AlertCircle } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import MultiInputHandle from './MultiInputHandle';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, need } from '../../utils/nodeRequirements';

const DURATIONS = [
  { value: -1, label: 'Auto' },
  { value: 4, label: '4s' },
  { value: 5, label: '5s' },
  { value: 6, label: '6s' },
  { value: 8, label: '8s' },
  { value: 10, label: '10s' },
  { value: 15, label: '15s' },
  { value: 20, label: '20s' },
  { value: 30, label: '30s' },
];

const RESOLUTIONS = ['480p', '720p', '1080p'];

const ASPECT_RATIOS = [
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' },
  { value: '1:1', label: '1:1' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '21:9', label: '21:9' },
  { value: 'adaptive', label: 'Adaptativo' },
];

const REF_LIMITS = { image: 30, video: 10, audio: 10 };
const REF_ACCEPT = {
  image: 'image/jpeg,image/jpg,image/png,image/webp',
  video: 'video/mp4,video/mov,video/webm',
  audio: 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/mpeg,audio/ogg',
};
const REF_LABEL = { image: 'Imagen', Video: 'Video' };

const inferKind = (src) => {
  const t = src?.outputType;
  if (t === 'video' || t === 'audio' || t === 'image') return t;
  const url = src?.outputUrl || src?.preview || '';
  if (/\.(mp4|mov|webm)(\?|#|$)/i.test(url)) return 'video';
  if (/\.(mp3|wav|m4a|aac|ogg|flac)(\?|#|$)/i.test(url)) return 'audio';
  return 'image';
};

const Seedance25Node = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [duration, setDuration] = useState(data.duration ?? 5);
  const [resolution, setResolution] = useState(data.resolution || '720p');
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '16:9');
  const [generateAudio, setGenerateAudio] = useState(data.generateAudio ?? true);
  const [watermark, setWatermark] = useState(!!data.watermark);
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'mp4');
  const [seed, setSeed] = useState(data.seed ?? '');
  const [firstFrame, setFirstFrame] = useState(data.firstFrame || null);
  const [lastFrame, setLastFrame] = useState(data.lastFrame || null);
  const [refs, setRefs] = useState(data.refs || []);
  const [lastPrompt, setLastPrompt] = useState(data.lastPrompt || '');

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openVideoPreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  // Connected inputs
  const connData = useCallback((handleId) => {
    const edge = edges.find(e => e.target === id && e.targetHandle === handleId);
    if (!edge) return null;
    const src = nodes.find(n => n.id === edge.source);
    return src?.data?.hasOutput ? src.data : null;
  }, [edges, nodes, id]);

  const firstFrameConn = connData('inputImage');
  const lastFrameConn = connData('inputLastFrame');
  const connectedRefs = useMemo(() => (
    edges
      .filter(e => e.target === id && e.targetHandle === 'refInput')
      .map(e => nodes.find(n => n.id === e.source)?.data)
      .filter(d => d?.hasOutput)
  ), [edges, nodes, id]);

  const effectiveFirst = firstFrameConn || firstFrame;
  const effectiveLast = lastFrameConn || lastFrame;
  const hasFrames = !!(effectiveFirst || effectiveLast);
  const hasRefs = refs.length + connectedRefs.length > 0;

  useEffect(() => {
    updateNodeData(id, {
      prompt, duration, resolution, aspectRatio, generateAudio, watermark,
      outputFormat, seed, firstFrame, lastFrame, refs,
    });
  }, [prompt, duration, resolution, aspectRatio, generateAudio, watermark, outputFormat, seed, firstFrame, lastFrame, refs]); // eslint-disable-line

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const pickFrame = useCallback((which) => {
    setSourcePickerHandler((result) => {
      const entry = result.file
        ? { file: result.file, preview: URL.createObjectURL(result.file), name: result.file.name }
        : { file: null, preview: result.url, name: result.fileName, fromGallery: true };
      if (which === 'first') setFirstFrame(entry); else setLastFrame(entry);
    });
    openSourcePicker(id, ['image'], REF_ACCEPT.image);
  }, [id, openSourcePicker]);

  const addRef = useCallback((kind) => {
    const count = refs.filter(r => r.kind === kind).length
      + connectedRefs.filter(d => inferKind(d) === kind).length;
    if (count >= REF_LIMITS[kind]) return;
    setSourcePickerHandler((result) => {
      const entry = result.file
        ? { kind, file: result.file, preview: URL.createObjectURL(result.file), name: result.file.name }
        : { kind, file: null, preview: result.url, name: result.fileName, fromGallery: true };
      setRefs(prev => [...prev, entry]);
    });
    openSourcePicker(id, [kind], REF_ACCEPT[kind]);
  }, [id, refs, connectedRefs, openSourcePicker]);

  const removeRef = useCallback((index) => {
    setRefs(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    const usedPrompt = prompt.trim();
    if (!usedPrompt && !hasFrames && !hasRefs) return false;
    resetExec();

    const files = [];
    const params = {
      duration: Number(duration),
      resolution,
      aspect_ratio: aspectRatio,
      generate_audio: generateAudio,
      watermark,
      output_format: outputFormat,
    };
    if (usedPrompt) params.prompt = usedPrompt;
    const seedNum = parseInt(seed, 10);
    if (seed !== '' && !Number.isNaN(seedNum)) params.seed = seedNum;

    // frames: file upload or direct URL
    const attachSingle = async (src, key, mime) => {
      if (!src) return;
      let file = src.file;
      if (!file) {
        const url = src.outputUrl || src.preview;
        if (url?.startsWith('http')) { params[key] = url; return; }
        if (url) file = await blobToFile(url, src.name || `${key}.bin`, mime);
      }
      if (file) files.push({ key, file });
    };
    await attachSingle(effectiveFirst, 'image_url', 'image/jpeg');
    await attachSingle(effectiveLast, 'last_frame_image_url', 'image/jpeg');

    // referencias agrupadas por tipo con índice propio
    const counters = { image: 0, video: 0, audio: 0 };
    const allRefs = [
      ...connectedRefs.map(d => ({ kind: inferKind(d), file: d.file, preview: d.outputUrl || d.preview, name: d.fileName })),
      ...refs,
    ];
    for (const r of allRefs) {
      const idx = counters[r.kind];
      if (idx >= REF_LIMITS[r.kind]) continue;
      counters[r.kind] += 1;
      await attachSingle(r, `reference_${r.kind}_${idx}`, r.kind === 'video' ? 'video/mp4' : r.kind === 'audio' ? 'audio/mpeg' : 'image/jpeg');
    }

    const result = await processNode({
      resultType: 'seedance_25',
      files,
      params,
    });

    if (result?.outputUrl) {
      setLastPrompt(usedPrompt);
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'video',
        lastPrompt: usedPrompt,
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'video',
        tool: 'seedance25',
        filename: `seedance25_${Date.now()}.${outputFormat || 'mp4'}`,
        url: result.outputUrl,
        prompt: usedPrompt,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, prompt, duration, resolution, aspectRatio, generateAudio, watermark, outputFormat, seed,
      effectiveFirst, effectiveLast, refs, connectedRefs, hasFrames, hasRefs,
      updateNodeData, addAsset, processNode, resetExec, setReconfigure]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const refCountByKind = (kind) =>
    refs.filter(r => r.kind === kind).length + connectedRefs.filter(d => inferKind(d) === kind).length;
  const canProcess = prompt.trim().length > 0 || hasFrames || hasRefs;
  const editingLike = !!effectiveLast || refCountByKind('video') > 0;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([need(prompt.trim().length > 0 || hasFrames || hasRefs, 'escribir el prompt o agregar un frame o una referencia')]);

  return (
    <div className={`node-container seedance25-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="seedance25-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Input handles: frame inicial / frame final / referencias multi */}
      <Handle type="target" position={Position.Left} id="inputImage" className="node-handle input-handle" style={{ top: '18%' }} data-testid="seedance25-input-image" />
      <Handle type="target" position={Position.Left} id="inputLastFrame" className="node-handle input-handle" style={{ top: '32%' }} data-testid="seedance25-input-lastframe" />
      <MultiInputHandle id="refInput" nodeId={id} limit={30} label="Referencias" accent="#22d3ee" top="55%" dataTestId="seedance25-ref-input" />

      {/* Header */}
      <div className="node-header seedance25-header">
        <div className="node-header-left">
          <Clapperboard size={16} />
          <span>Video seedance-2.5</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-seedance25">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content seedance25-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivos...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Generando video...'}
              </span>
            </div>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result">
            <video
              src={data.outputUrl}
              controls
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'Video seedance-2.5')}
            />
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openVideoPreview(data.outputUrl, 'Video seedance-2.5')} data-testid="seedance25-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `seedance25_${Date.now()}.${outputFormat || 'mp4'}`); }} data-testid="seedance25-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="video" label="seedance25" testIdPrefix="seedance25-timeline" />
            </div>
            <div className="nano-regen-section">
              <span className="nano-section-label">Prompt usado:</span>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="seedance25"
                label="Prompt de regeneración"
                className="nano-prompt-textarea nano-regen-textarea"
                rows={2}
                testId="seedance25-regen-prompt"
              />
              <button className="nano-regen-btn seedance25-regen-btn" onClick={handleRegenerate} data-testid="seedance25-regen-btn">
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="seedance25-reconfigure-btn" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Video creado</span>
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={null}
                onCancel={() => setReconfigure(false)}
                testId="seedance25-reconfigure-notice"
              />
            )}

            {/* Frames inicio / fin (excluyente con referencias) */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <ImagePlus size={12} />
                Frames inicio / fin — opcional
              </span>
              <div className="nano-images-row">
                {(effectiveFirst || !hasRefs) && (
                  effectiveFirst ? (
                    <div className="nano-img-thumb" title="Frame inicial">
                      <img src={effectiveFirst.preview || effectiveFirst.outputUrl} alt="Frame inicial" />
                      {!firstFrameConn && (
                        <button className="nano-clear-btn" onClick={() => setFirstFrame(null)} data-testid="seedance25-clear-first">
                          <X size={8} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <button className="nano-add-img-btn" onClick={() => pickFrame('first')} disabled={hasRefs} title="Frame inicial" data-testid="seedance25-add-first">
                      <Plus size={14} />
                    </button>
                  )
                )}
                {(effectiveLast || (effectiveFirst && !hasRefs)) && (
                  effectiveLast ? (
                    <div className="nano-img-thumb" title="Frame final">
                      <img src={effectiveLast.preview || effectiveLast.outputUrl} alt="Frame final" />
                      {!lastFrameConn && (
                        <button className="nano-clear-btn" onClick={() => setLastFrame(null)} data-testid="seedance25-clear-last">
                          <X size={8} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <button className="nano-add-img-btn" onClick={() => pickFrame('last')} disabled={!effectiveFirst || hasRefs} title="Frame final (requiere frame inicial)" data-testid="seedance25-add-last">
                      <Plus size={14} />
                    </button>
                  )
                )}
              </div>
              {firstFrameConn && <span className="seedance25-conn-note">Frame inicial conectado</span>}
              {lastFrameConn && <span className="seedance25-conn-note">Frame final conectado</span>}
            </div>

            {/* Referencias multimodales (excluyente con frames) */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <Film size={12} />
                Referencias ({refs.length + connectedRefs.length}) — opcional
              </span>
              <div className="nano-images-row">
                {refs.map((r, index) => (
                  <div key={r.name || `ref-${index}`} className="nano-img-thumb" title={`${r.kind}: ${r.name || ''}`}>
                    {r.kind === 'image' ? (
                      <img src={r.preview} alt={`Ref ${index + 1}`} />
                    ) : (
                      <span className="seedance25-ref-icon">{r.kind === 'video' ? <Film size={14} /> : <Music size={14} />}</span>
                    )}
                    <button className="nano-clear-btn" onClick={() => removeRef(index)} data-testid={`seedance25-remove-ref-${index}`}>
                      <X size={8} />
                    </button>
                  </div>
                ))}
                {!hasFrames && (
                  <div className="seedance25-ref-adds">
                    <button className="nano-add-img-btn" onClick={() => addRef('image')} disabled={refCountByKind('image') >= REF_LIMITS.image} title={`Imagen ref (${refCountByKind('image')}/30)`} data-testid="seedance25-add-ref-image">
                      <ImagePlus size={13} />
                    </button>
                    <button className="nano-add-img-btn" onClick={() => addRef('video')} disabled={refCountByKind('video') >= REF_LIMITS.video} title={`Video ref (${refCountByKind('video')}/10)`} data-testid="seedance25-add-ref-video">
                      <Film size={13} />
                    </button>
                    <button className="nano-add-img-btn" onClick={() => addRef('audio')} disabled={refCountByKind('audio') >= REF_LIMITS.audio} title={`Audio ref (${refCountByKind('audio')}/10)`} data-testid="seedance25-add-ref-audio">
                      <Music size={13} />
                    </button>
                  </div>
                )}
              </div>
              <span className="seedance25-hint">Citalas en el prompt como [Image1], [Video1], [Audio1]</span>
            </div>

            {(hasFrames && refs.length > 0) || (hasRefs && (firstFrame || lastFrame)) ? (
              <div className="videoSound-warning">
                <AlertCircle size={12} />
                <span>Frames y referencias son excluyentes: se usarán los frames</span>
              </div>
            ) : null}

            {/* Prompt */}
            <div className="nano-prompt-section">
              <div className="nano-prompt-header">
                <span className="nano-section-label">Prompt {hasFrames || hasRefs ? '(opcional con media)' : '*'}</span>
                <DirectorsTrigger nodeId={id} nodeType="seedance25" currentPrompt={prompt} onInsert={setPrompt} />
              </div>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="seedance25"
                placeholder="Production brief: escena, sujetos, cámara, luz, acciones. Diálogo entre comillas: dice &quot;hola&quot;..."
                className="nano-prompt-textarea"
                rows={3}
                testId="seedance25-prompt"
              />
            </div>

            {/* Opciones */}
            <div className="nano-options-row seedance25-options">
              <div className="nano-option">
                <span className="nano-option-label">Duración</span>
                <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="nano-select" data-testid="seedance25-duration">
                  {DURATIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Resolución</span>
                <select value={resolution} onChange={(e) => setResolution(e.target.value)} className="nano-select" data-testid="seedance25-resolution">
                  {RESOLUTIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Ratio</span>
                <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="nano-select" data-testid="seedance25-aspect-ratio">
                  {ASPECT_RATIOS.map((ar) => (
                    <option key={ar.value} value={ar.value}>{ar.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Formato</span>
                <select value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)} className="nano-select" data-testid="seedance25-output-format">
                  <option value="mp4">MP4</option>
                </select>
              </div>
            </div>

            {editingLike && (
              <div className="videoSound-warning" data-testid="seedance25-adaptive-note">
                <AlertCircle size={12} />
                <span>Con frame final o videos de referencia se fuerza ratio Adaptativo y duración Auto</span>
              </div>
            )}

            {/* Toggles + seed */}
            <div className="banana2pro-toggles">
              <label className="banana2pro-toggle seedance25-toggle" title="Audio nativo sincronizado: diálogo (entre comillas), efectos y música">
                <input type="checkbox" checked={generateAudio} onChange={(e) => setGenerateAudio(e.target.checked)} data-testid="seedance25-generate-audio" />
                <Music size={11} />
                <span>Audio</span>
              </label>
              <label className="banana2pro-toggle seedance25-toggle" title="Agregar marca de agua al video">
                <input type="checkbox" checked={watermark} onChange={(e) => setWatermark(e.target.checked)} data-testid="seedance25-watermark" />
                <span>Watermark</span>
              </label>
              <input
                type="number"
                className="nano-select seedance25-seed nodrag nowheel"
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                placeholder="Seed (vacío = aleatorio)"
                title="Semilla para reproducibilidad (no garantizada)"
                data-testid="seedance25-seed"
              />
            </div>

            <button
              className="node-process-btn seedance25-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="seedance25-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure ? 'Regenerar con cambios' : 'Generar video'}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

Seedance25Node.displayName = 'Seedance25Node';

export default Seedance25Node;
